// AssetSetManager.ts
//
// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as Interfaces from '../../Interfaces/Interfaces';
import * as Enumerations from '../../Enumerations/Enumerations';
interface IKnowledgeViewAssetSet {
    ID: string;
    Ord: number;
    Color?: number; // packed numeric color (e.g. 0xRRGGBB or 0xAARRGGBB)
    Hidden?: boolean;
    MaterialID?: string | null;
}

interface IAssetSetDefinition {
    id: string;
    priority: number;
    colorKey?: number;
    materialID?: string | null;
    active: boolean;

    layerSource: string;
    layerPriority: number;
    hidden?: boolean;

    /** If true, this set applies to ALL assets (there should only be one). */
    isAll3DAssets?: boolean;
}

interface IAssetGeometryDecision {
    excluded: boolean;
    ownerSetId: string | null;
    sourceSetIds: string[];
}

interface IBatchableOverrideManager {
    beginBatch?: () => void;
    endBatch?: () => void;
    setGlobalOverrideForSource?: (source: string, priority: number, materials: string[]) => void;
    clearGlobalOverrideForSource?: (source: string) => void;
}

const UNASSIGNED_ASSET_LAYER_ID = "__asset_set_unassigned__";
const ALL_ASSETS_LOAD_PRIORITY = Number.MAX_SAFE_INTEGER;

class AssetSetManager implements Interfaces.Interfaces.IAssetSetManager {

    private _meshManager: Interfaces.Interfaces.IMeshManager;
    private _materialManager: Interfaces.Interfaces.IMaterialsManager;
    private _overrideManager: Interfaces.Interfaces.IMaterialOverrideManager;

    // assetSetId -> definition
    private _assetSets: { [assetSetId: string]: IAssetSetDefinition } = {};

    // assetSetId -> { assetId: true }  (explicit members only)
    private _assetSetAssets: { [assetSetId: string]: { [assetId: string]: boolean } } = {};

    // assetId -> { assetSetId: true }  (reverse mapping for explicit memberships)
    private _assetToSets: { [assetId: string]: { [assetSetId: string]: boolean } } = {};

    // Assets hidden by Display-layer visibility. This ownership map prevents
    // clearing a manual viewer hide when a set becomes visible again.
    private _visibilityHiddenAssets: { [assetId: string]: boolean } = {};
    private _strictVisibilityActive = false;

    // Special: at most one set can be marked as "All Assets"
    private _allAssetsSetId: string | null = null;

    // Geometry ownership is independent of material priority. Existing owners
    // remain sticky while their source set is active; this prevents a reorder
    // or newly-added overlapping set from forcing geometry churn.
    private _geometryDecisionByAsset: { [assetId: string]: IAssetGeometryDecision } = {};
    private _geometryOwnershipEpochBySet: { [assetSetId: string]: number } = {};
    private _geometryOwnershipRevision = 0;
    private _geometryAllowedCountBySet: { [assetSetId: string]: number } = {};
    private _geometryDefaultUnownedHidden = false;

    constructor(
        meshManager: Interfaces.Interfaces.IMeshManager,
        materialManager: Interfaces.Interfaces.IMaterialsManager,
        overrideManager: Interfaces.Interfaces.IMaterialOverrideManager
    ) {
        this._meshManager = meshManager;
        this._materialManager = materialManager;
        this._overrideManager = overrideManager;
    }

    // ---------------------------------------------------------------------
    //  Internal: batching helper
    // ---------------------------------------------------------------------

    private _withOverrideBatch(fn: () => void): void {
        const om: any = this._overrideManager as any;
        const canBatch = !!om && typeof om.beginBatch === "function" && typeof om.endBatch === "function";

        if (!canBatch) {
            fn();
            return;
        }

        om.beginBatch();
        try {
            fn();
        } finally {
            om.endBatch();
        }
    }

    // ---------------------------------------------------------------------
    //  Lifecycle
    // ---------------------------------------------------------------------

    public dispose(): void {
        // dispose can clear lots of layers; batch it
        this._withOverrideBatch(() => {
            this._clearAssetSetVisibility();

            // Remove all per-asset layers we created for explicit memberships
            for (const setId in this._assetSetAssets) {
                const def = this._assetSets[setId];
                if (!def) continue;

                const members = this._assetSetAssets[setId];
                if (!members) continue;

                for (const assetId in members) {
                    this._overrideManager.clearOverrideForSource(assetId, def.layerSource);
                }
            }

            // Remove global All-Assets layer if present
            if (this._allAssetsSetId) {
                const allDef = this._assetSets[this._allAssetsSetId];
                if (allDef) {
                    const omAny: any = this._overrideManager;
                    if (typeof omAny.clearGlobalOverrideForSource === "function") {
                        omAny.clearGlobalOverrideForSource(allDef.layerSource);
                    }
                }
            }
        });

        this._meshManager = null as any;
        this._materialManager = null as any;
        this._overrideManager = null as any;
        this._assetSets = {};
        this._assetSetAssets = {};
        this._assetToSets = {};
        this._allAssetsSetId = null;
    }

    // ---------------------------------------------------------------------
    //  Public API: asset set definitions
    // ---------------------------------------------------------------------

    public getAllSetID() {
        return this._allAssetsSetId;
    }

    public registerAssetSet(
        assetSetId: string,
        priority: number,
        colorKey?: number,
        active: boolean = true,
        materialID?: string | null
    ): void {

        if (!assetSetId) return;

        if (!this._assetSets[assetSetId]) {
            this._assetSets[assetSetId] = {
                id: assetSetId,
                priority,
                colorKey,
                materialID,
                active,
                layerSource: `assetSet:${assetSetId}`,
                layerPriority: priority,
                isAll3DAssets: false // promoted only by membership population
            };
        } else {
            const def = this._assetSets[assetSetId];
            def.priority = priority;
            def.layerPriority = priority;
            // Octree registration does not carry appearance data. Preserve the
            // current color when it is omitted so rebuilding enabled roots
            // cannot briefly reset an established override to its base material.
            // An explicit 0 remains meaningful and is still applied.
            if (colorKey !== undefined) {
                def.colorKey = colorKey;
            }
            if (materialID !== undefined) {
                def.materialID = materialID;
            }
            def.active = active;

            if (!def.layerSource) def.layerSource = `assetSet:${assetSetId}`;
            // DO NOT touch isAll3DAssets here
        }

        // If this happens to be the all-assets set, we may need to update global layer state
        if (this._allAssetsSetId === assetSetId) {
            this._withOverrideBatch(() => this._syncAllAssetsGlobalLayer());
        } else {
            // For explicit sets, if membership exists, re-apply this set's layer to its members
            const members = this._assetSetAssets[assetSetId];
            if (members) {
                // batch if big enough
                const ids = Object.keys(members);
                if (ids.length > 50) {
                    this._withOverrideBatch(() => this._applySetLayerToAssets(assetSetId, ids));
                } else {
                    this._applySetLayerToAssets(assetSetId, ids);
                }
            }
        }
    }

    public isSetEnabled(assetSetId: string): boolean {
        const allDef = this._allAssetsSetId
            ? this._assetSets[this._allAssetsSetId]
            : null;

        if (this._shouldSetHaveEffectiveLayer(allDef) && !allDef?.hidden) {
            return true;
        }

        if (assetSetId === UNASSIGNED_ASSET_LAYER_ID) {
            return false;
        }

        const def = this._assetSets[assetSetId];
        if (assetSetId === this._allAssetsSetId && def?.hidden) {
            return false;
        }
        return this._shouldSetHaveEffectiveLayer(def);
    }

    public getGeometryOwnershipEpoch(assetSetId: string): number {
        return this._geometryOwnershipEpochBySet[assetSetId] || 0;
    }

    public isAssetGeometryAllowed(assetId: string, sourceAssetSetId: string): boolean {
        if (!assetId || !sourceAssetSetId) return true;

        const decision = this._geometryDecisionByAsset[assetId];
        if (!decision) return true;
        if (decision.excluded) return false;
        if (sourceAssetSetId === UNASSIGNED_ASSET_LAYER_ID || sourceAssetSetId === this._allAssetsSetId) {
            return decision.ownerSetId
                ? decision.ownerSetId === sourceAssetSetId
                : decision.sourceSetIds.length === 0;
        }
        if (decision.ownerSetId) return decision.ownerSetId === sourceAssetSetId;
        if (decision.sourceSetIds.length <= 1) {
            return decision.sourceSetIds.length === 0 || decision.sourceSetIds[0] === sourceAssetSetId;
        }
        return decision.ownerSetId === sourceAssetSetId;
    }

    public isSetGeometryEnabled(assetSetId: string): boolean {
        if (!this.isSetEnabled(assetSetId)) return false;
        if (assetSetId === UNASSIGNED_ASSET_LAYER_ID || assetSetId === this._allAssetsSetId) {
            return true;
        }
        return (this._geometryAllowedCountBySet[assetSetId] || 0) > 0;
    }

    public refreshGeometryOwnership(): Interfaces.Interfaces.IAssetGeometryOwnershipSnapshot {
        const previous = this._geometryDecisionByAsset;
        const next: { [assetId: string]: IAssetGeometryDecision } = {};
        const nextAllowedCounts: { [assetSetId: string]: number } = {};
        const changedSets: { [assetSetId: string]: boolean } = {};
        const defaultUnownedGeometryHidden = this._shouldHideUnownedGeometry();
        const allAssetsGeometrySourceEnabled = !!this._allAssetsSetId && this.isSetEnabled(this._allAssetsSetId);
        const allAssetIds = new Set([
            ...Object.keys(previous),
            ...Object.keys(this._assetToSets),
            ...this._meshManager.getLoadedAssetIds(),
        ]);

        for (const assetId of allAssetIds) {
            const sourceSetIds = Object.keys(this._assetToSets[assetId] || {})
                .filter(setId => setId !== this._allAssetsSetId && this.isSetEnabled(setId))
                .sort((left, right) => {
                    const leftPriority = this._assetSets[left]?.layerPriority ?? Number.MAX_SAFE_INTEGER;
                    const rightPriority = this._assetSets[right]?.layerPriority ?? Number.MAX_SAFE_INTEGER;
                    return leftPriority - rightPriority || left.localeCompare(right);
                });
            const excluded = this._shouldExcludeAssetGeometry(assetId);
            const oldOwner = previous[assetId]?.ownerSetId;
            const eligibleOwnerSetIds = [
                ...sourceSetIds,
                ...(allAssetsGeometrySourceEnabled && this._allAssetsSetId ? [this._allAssetsSetId] : []),
            ];
            const ownerSetId = !excluded && eligibleOwnerSetIds.length > 0
                ? (oldOwner && eligibleOwnerSetIds.indexOf(oldOwner) >= 0
                    ? oldOwner
                    : (allAssetsGeometrySourceEnabled && this._allAssetsSetId
                        ? this._allAssetsSetId
                        : eligibleOwnerSetIds[0]))
                : null;
            const decision: IAssetGeometryDecision = { excluded, ownerSetId, sourceSetIds };

            if (ownerSetId || sourceSetIds.length > 0 || excluded) {
                next[assetId] = decision;
            }
            if (ownerSetId) {
                nextAllowedCounts[ownerSetId] = (nextAllowedCounts[ownerSetId] || 0) + 1;
            }

            const previousDecision = previous[assetId];
            const previousSources = new Set(previousDecision?.sourceSetIds || []);
            const nextSources = new Set(sourceSetIds);
            for (const sourceSetId of previousSources) {
                if (!nextSources.has(sourceSetId)) changedSets[sourceSetId] = true;
            }
            for (const sourceSetId of nextSources) {
                if (!previousSources.has(sourceSetId)) changedSets[sourceSetId] = true;
            }
            const candidateSets = new Set([
                ...(previousDecision?.sourceSetIds || []),
                ...sourceSetIds,
                ...(previousDecision?.ownerSetId ? [previousDecision.ownerSetId] : []),
                ...(ownerSetId ? [ownerSetId] : []),
                UNASSIGNED_ASSET_LAYER_ID,
                ...(this._allAssetsSetId ? [this._allAssetsSetId] : []),
            ]);
            for (const sourceSetId of candidateSets) {
                const wasAllowed = this._isGeometryDecisionAllowed(
                    previousDecision,
                    sourceSetId,
                    this._geometryDefaultUnownedHidden);
                const isAllowed = this._isGeometryDecisionAllowed(
                    decision,
                    sourceSetId,
                    defaultUnownedGeometryHidden);
                if (wasAllowed !== isAllowed) changedSets[sourceSetId] = true;
            }
        }

        if (defaultUnownedGeometryHidden !== this._geometryDefaultUnownedHidden) {
            changedSets[UNASSIGNED_ASSET_LAYER_ID] = true;
            if (this._allAssetsSetId) changedSets[this._allAssetsSetId] = true;
        }

        const changedAssetSetIds = Object.keys(changedSets);
        if (changedAssetSetIds.length > 0) {
            this._geometryOwnershipRevision++;
            for (const setId of changedAssetSetIds) {
                this._geometryOwnershipEpochBySet[setId] = (this._geometryOwnershipEpochBySet[setId] || 0) + 1;
            }
        }

        this._geometryDecisionByAsset = next;
        this._geometryAllowedCountBySet = nextAllowedCounts;
        this._geometryDefaultUnownedHidden = defaultUnownedGeometryHidden;

        const ownerByAssetId: { [assetId: string]: string } = {};
        for (const assetId in next) {
            const decision = next[assetId];
            if (decision.excluded) {
                ownerByAssetId[assetId] = "";
            }
            else if (decision.ownerSetId &&
                (decision.sourceSetIds.length > 1 ||
                    (allAssetsGeometrySourceEnabled && decision.sourceSetIds.length > 0))) {
                ownerByAssetId[assetId] = decision.ownerSetId;
            }
        }

        return {
            revision: this._geometryOwnershipRevision,
            ownerByAssetId,
            epochByAssetSetId: { ...this._geometryOwnershipEpochBySet },
            changedAssetSetIds,
            defaultUnownedGeometryHidden,
            fallbackSourceAssetSetIds: [
                UNASSIGNED_ASSET_LAYER_ID,
                ...(this._allAssetsSetId ? [this._allAssetsSetId] : []),
            ],
        };
    }

    private _shouldHideUnownedGeometry(): boolean {
        const allDefinition = this._allAssetsSetId ? this._assetSets[this._allAssetsSetId] : null;
        return !!allDefinition?.hidden ||
            (this._strictVisibilityActive && !this._shouldSetHaveEffectiveLayer(allDefinition));
    }

    private _isGeometryDecisionAllowed(
        decision: IAssetGeometryDecision | null | undefined,
        sourceAssetSetId: string,
        defaultUnownedGeometryHidden: boolean = this._geometryDefaultUnownedHidden
    ): boolean {
        if (sourceAssetSetId === UNASSIGNED_ASSET_LAYER_ID || sourceAssetSetId === this._allAssetsSetId) {
            if (defaultUnownedGeometryHidden || decision?.excluded) return false;
            return decision?.ownerSetId
                ? decision.ownerSetId === sourceAssetSetId
                : !decision || decision.sourceSetIds.length === 0;
        }
        if (!decision || decision.excluded) return false;
        if (decision.ownerSetId) return decision.ownerSetId === sourceAssetSetId;
        if (decision.sourceSetIds.length === 1) return decision.sourceSetIds[0] === sourceAssetSetId;
        return decision.ownerSetId === sourceAssetSetId;
    }

    public setAssetSetActive(assetSetId: string, active: boolean): void {
        const def = this._assetSets[assetSetId];
        if (!def || def.active === active) return;

        def.active = active;

        if (def.isAll3DAssets) {
            this._syncAllAssetsGlobalLayer();
            return;
        }

        const members = this._assetSetAssets[assetSetId];
        if (members) {
            const ids = Object.keys(members);
            if (ids.length > 50) {
                this._withOverrideBatch(() => this._applySetLayerToAssets(assetSetId, ids));
            } else {
                this._applySetLayerToAssets(assetSetId, ids);
            }
        }
    }

    public setAssetSetPriority(assetSetId: string, priority: number): void {
        const def = this._assetSets[assetSetId];
        if (!def || def.priority === priority) return;

        def.priority = priority;
        def.layerPriority = priority;

        if (def.isAll3DAssets) {
            this._syncAllAssetsGlobalLayer();
            return;
        }

        const members = this._assetSetAssets[assetSetId];
        if (members) {
            const ids = Object.keys(members);
            if (ids.length > 50) {
                this._withOverrideBatch(() => this._applySetLayerToAssets(assetSetId, ids));
            } else {
                this._applySetLayerToAssets(assetSetId, ids);
            }
        }
    }

    public setAssetSetColor(assetSetId: string, colorKey?: number): void {
        const def = this._assetSets[assetSetId];
        if (!def) return;

        def.colorKey = colorKey;

        if (def.isAll3DAssets) {
            this._syncAllAssetsGlobalLayer();
            return;
        }

        const members = this._assetSetAssets[assetSetId];
        if (members) {
            const ids = Object.keys(members);
            if (ids.length > 50) {
                this._withOverrideBatch(() => this._applySetLayerToAssets(assetSetId, ids));
            } else {
                this._applySetLayerToAssets(assetSetId, ids);
            }
        }
    }

    public setAssetSetMaterial(assetSetId: string, materialID?: string | null): void {
        const def = this._assetSets[assetSetId];
        if (!def) return;

        def.materialID = materialID;

        if (def.isAll3DAssets) {
            this._syncAllAssetsGlobalLayer();
            return;
        }

        const members = this._assetSetAssets[assetSetId];
        if (members) {
            const ids = Object.keys(members);
            if (ids.length > 50) {
                this._withOverrideBatch(() => this._applySetLayerToAssets(assetSetId, ids));
            } else {
                this._applySetLayerToAssets(assetSetId, ids);
            }
        }
    }

    public destroyAllAssetSets(): void {
        // Whole operation can be large; batch it
        this._withOverrideBatch(() => {
            this._clearAssetSetVisibility();

            // Snapshot keys first because removeAssetSet mutates _assetSets/_assetSetAssets
            const allIds = Object.keys(this._assetSets);

            if (allIds.length === 0) return;

            // Remove the all-assets set first (if any) to clear global override cleanly
            if (this._allAssetsSetId) {
                const allId = this._allAssetsSetId;
                if (this._assetSets[allId]) {
                    this.removeAssetSet(allId);
                }
            }

            // Remove remaining sets
            for (let i = 0; i < allIds.length; i++) {
                const id = allIds[i];
                if (!id) continue;
                // all-assets already removed above
                if (id === this._allAssetsSetId) continue;
                if (this._assetSets[id]) {
                    this.removeAssetSet(id);
                }
            }

            // At this point removeAssetSet should have cleared everything,
            // but ensure we're fully reset (defensive).
            this._assetSets = {};
            this._assetSetAssets = {};
            this._assetToSets = {};
            this._allAssetsSetId = null;
        });
    }

    public resetAllAssetSetVisuals(): void {
        this._withOverrideBatch(() => {
            // reset defs
            for (const setId in this._assetSets) {
                const def = this._assetSets[setId];
                def.priority = 0;
                def.layerPriority = 0;
                def.colorKey = undefined;
                def.materialID = null;
            }

            // clear per-asset layers for all explicit memberships
            for (const setId in this._assetSetAssets) {
                const def = this._assetSets[setId];
                if (!def) continue;

                const members = this._assetSetAssets[setId];
                if (!members) continue;

                for (const assetId in members) {
                    this._overrideManager.clearOverrideForSource(assetId, def.layerSource);
                }
            }

            // clear/sync global layer (keep inside batch!)
            this._syncAllAssetsGlobalLayer();
        });
    }

    public setAssetSets(assetSetData: { id: string; assetIds: string[]; isAll3DAssets?: boolean }[]): void {
        this._withOverrideBatch(() => {
            const incomingRows = assetSetData || [];
            const incomingIds: { [assetSetId: string]: boolean } = {};

            // Identify all-assets (enforce at most one)
            let foundAll: string | null = null;

            for (const row of incomingRows) {
                if (!row || !row.id) continue;
                incomingIds[row.id] = true;

                if (!this._assetSets[row.id]) {
                    this._assetSets[row.id] = {
                        id: row.id,
                        priority: 0,
                        colorKey: undefined,
                        materialID: null,
                        active: true,
                        layerSource: `assetSet:${row.id}`,
                        layerPriority: 0,
                        isAll3DAssets: false
                    };
                } else {
                    const d = this._assetSets[row.id];
                    if (!d.layerSource) d.layerSource = `assetSet:${row.id}`;
                    if (d.layerPriority === undefined || d.layerPriority === null) d.layerPriority = d.priority;
                }

                const def = this._assetSets[row.id];

                if (row.isAll3DAssets) {
                    if (!foundAll) {
                        foundAll = row.id;
                        def.isAll3DAssets = true;
                        def.active = true;
                        if (def.priority <= 0) def.priority = ALL_ASSETS_LOAD_PRIORITY;
                        if (def.layerPriority <= 0) def.layerPriority = ALL_ASSETS_LOAD_PRIORITY;
                    } else {
                        def.isAll3DAssets = false; // ignore extras
                    }
                } else {
                    def.isAll3DAssets = false;
                }
            }

            for (const setId of Object.keys(this._assetSets)) {
                if (!incomingIds[setId]) {
                    this.removeAssetSet(setId);
                }
            }

            if (this._allAssetsSetId && this._allAssetsSetId !== foundAll) {
                const allDef = this._assetSets[this._allAssetsSetId];
                const om: any = this._overrideManager;
                if (allDef && typeof om.clearGlobalOverrideForSource === "function") {
                    om.clearGlobalOverrideForSource(allDef.layerSource);
                }
            }

            this._allAssetsSetId = foundAll;

            // Build explicit memberships for non-all sets
            for (const row of incomingRows) {
                if (!row || !row.id) continue;
                if (row.id === this._allAssetsSetId) {
                    const def = this._assetSets[row.id];
                    const members = this._assetSetAssets[row.id];
                    if (def && members) {
                        for (const assetId in members) {
                            this._overrideManager.clearOverrideForSource(assetId, def.layerSource);
                            if (this._assetToSets[assetId]) {
                                delete this._assetToSets[assetId][row.id];
                                if (Object.keys(this._assetToSets[assetId]).length === 0) {
                                    delete this._assetToSets[assetId];
                                }
                            }
                        }
                    }
                    this._assetSetAssets[row.id] = {};
                    continue;
                }

                const uniqueAssetIds = Array.from(new Set(row.assetIds || []));
                this.setAssetsForSet(row.id, uniqueAssetIds);
            }

            // Finally, sync the global layer based on the (single) all-assets def
            this._syncAllAssetsGlobalLayer();
        });
    }

    /** Apply only changed memberships; retained octree roots are independent. */
    public applyAssetSetMembershipDelta(
        activatedSets: { id: string; assetIds: string[]; isAll3DAssets?: boolean }[],
        deactivatedSetIds: string[]
    ): void {
        this._withOverrideBatch(() => {
            for (const assetSetId of deactivatedSetIds || []) {
                this.removeAssetSet(assetSetId);
            }

            for (const row of activatedSets || []) {
                if (!row?.id) continue;

                if (row.isAll3DAssets) {
                    if (this._allAssetsSetId && this._allAssetsSetId !== row.id) {
                        this.removeAssetSet(this._allAssetsSetId);
                    }
                    this.registerAssetSet(row.id, ALL_ASSETS_LOAD_PRIORITY, undefined, true);
                    const def = this._assetSets[row.id];
                    def.isAll3DAssets = true;
                    if (def.priority <= 0) def.priority = ALL_ASSETS_LOAD_PRIORITY;
                    if (def.layerPriority <= 0) def.layerPriority = ALL_ASSETS_LOAD_PRIORITY;
                    this._allAssetsSetId = row.id;
                    this._assetSetAssets[row.id] = {};
                    continue;
                }

                this.setAssetsForSet(row.id, Array.from(new Set(row.assetIds || [])));
            }

            this._syncAllAssetsGlobalLayer();
        });
    }

    public setAssetsForSet(assetSetId: string, assetIds: string[]): void {
        if (!assetSetId) return;

        // If this is the all-assets set, do not build explicit membership.
        if (this._allAssetsSetId === assetSetId) {
            this._assetSetAssets[assetSetId] = {};
            return;
        }

        this._withOverrideBatch(() => {
            if (!this._assetSets[assetSetId]) {
                this._assetSets[assetSetId] = {
                    id: assetSetId,
                    priority: 0,
                    colorKey: undefined,
                    materialID: null,
                    active: true,
                    layerSource: `assetSet:${assetSetId}`,
                    layerPriority: 0,
                    isAll3DAssets: false
                };
            }

            const def = this._assetSets[assetSetId];
            if (!def.layerSource) def.layerSource = `assetSet:${assetSetId}`;
            if (def.layerPriority === undefined || def.layerPriority === null) def.layerPriority = def.priority;

            const oldMembers = this._assetSetAssets[assetSetId] || {};
            const newMembers: { [assetId: string]: boolean } = {};
            this._assetSetAssets[assetSetId] = newMembers;

            const affected: { [assetId: string]: boolean } = {};

            // add
            for (let i = 0; i < (assetIds || []).length; i++) {
                const assetId = assetIds[i];
                if (!assetId) continue;

                newMembers[assetId] = true;
                affected[assetId] = true;

                if (!this._assetToSets[assetId]) this._assetToSets[assetId] = {};
                this._assetToSets[assetId][assetSetId] = true;
            }

            // remove old
            for (const oldAssetId in oldMembers) {
                if (!newMembers[oldAssetId]) {
                    affected[oldAssetId] = true;

                    if (this._assetToSets[oldAssetId]) {
                        delete this._assetToSets[oldAssetId][assetSetId];
                        if (Object.keys(this._assetToSets[oldAssetId]).length === 0) {
                            delete this._assetToSets[oldAssetId];
                        }
                    }
                }
            }

            // Apply this set's layer for affected assets
            this._applySetLayerToAssets(assetSetId, Object.keys(affected));
        });
    }

    public addAssetsToSet(assetSetId: string, assetIds: string[]): void {
        if (!assetSetId) return;
        if (this._allAssetsSetId === assetSetId) return; // no explicit membership

        // Might be large: batch
        this._withOverrideBatch(() => {
            if (!this._assetSets[assetSetId]) {
                this._assetSets[assetSetId] = {
                    id: assetSetId,
                    priority: 0,
                    colorKey: undefined,
                    materialID: null,
                    active: true,
                    layerSource: `assetSet:${assetSetId}`,
                    layerPriority: 0,
                    isAll3DAssets: false
                };
            }

            if (!this._assetSetAssets[assetSetId]) this._assetSetAssets[assetSetId] = {};

            const members = this._assetSetAssets[assetSetId];
            const affected: { [assetId: string]: boolean } = {};

            for (let i = 0; i < (assetIds || []).length; i++) {
                const assetId = assetIds[i];
                if (!assetId) continue;

                if (!members[assetId]) {
                    members[assetId] = true;
                    affected[assetId] = true;

                    if (!this._assetToSets[assetId]) this._assetToSets[assetId] = {};
                    this._assetToSets[assetId][assetSetId] = true;
                }
            }

            this._applySetLayerToAssets(assetSetId, Object.keys(affected));
        });
    }

    public removeAssetsFromSet(assetSetId: string, assetIds: string[]): void {
        if (!assetSetId) return;
        if (this._allAssetsSetId === assetSetId) return;

        const members = this._assetSetAssets[assetSetId];
        if (!members) return;

        this._withOverrideBatch(() => {
            const affected: { [assetId: string]: boolean } = {};

            for (let i = 0; i < (assetIds || []).length; i++) {
                const assetId = assetIds[i];
                if (!assetId) continue;

                if (members[assetId]) {
                    delete members[assetId];
                    affected[assetId] = true;

                    if (this._assetToSets[assetId]) {
                        delete this._assetToSets[assetId][assetSetId];
                        if (Object.keys(this._assetToSets[assetId]).length === 0) {
                            delete this._assetToSets[assetId];
                        }
                    }
                }
            }

            this._applySetLayerToAssets(assetSetId, Object.keys(affected));
        });
    }

    public removeAssetSet(assetSetId: string): void {
        if (!assetSetId) return;

        this._withOverrideBatch(() => {
            const def = this._assetSets[assetSetId];
            const members = this._assetSetAssets[assetSetId];

            // If all-assets: clear global and remove
            if (this._allAssetsSetId === assetSetId) {
                const om: any = this._overrideManager;
                if (def && typeof om.clearGlobalOverrideForSource === "function") {
                    om.clearGlobalOverrideForSource(def.layerSource);
                }
                this._allAssetsSetId = null;
            } else {
                // clear per-asset layers for its members
                if (def && members) {
                    for (const assetId in members) {
                        this._overrideManager.clearOverrideForSource(assetId, def.layerSource);
                        if (this._assetToSets[assetId]) {
                            delete this._assetToSets[assetId][assetSetId];
                            if (Object.keys(this._assetToSets[assetId]).length === 0) {
                                delete this._assetToSets[assetId];
                            }
                        }
                    }
                }
            }

            delete this._assetSets[assetSetId];
            delete this._assetSetAssets[assetSetId];

            // If we removed all-assets, sync (should now be off)
            if (!this._allAssetsSetId) {
                this._syncAllAssetsGlobalLayer();
            }
        });
    }

    /**
     * Forget all sets/memberships AND clear all layers we introduced (per-asset + global).
     */
    public clearAllAssetSets(preserveVisibility: boolean = false): void {
        this._withOverrideBatch(() => {
            if (!preserveVisibility) {
                this._clearAssetSetVisibility();
            }

            // clear per-asset layers for all explicit memberships
            for (const setId in this._assetSetAssets) {
                const def = this._assetSets[setId];
                if (!def) continue;

                const members = this._assetSetAssets[setId];
                if (!members) continue;

                for (const assetId in members) {
                    this._overrideManager.clearOverrideForSource(assetId, def.layerSource);
                }
            }

            // clear global layer
            if (this._allAssetsSetId) {
                const allDef = this._assetSets[this._allAssetsSetId];
                const om: any = this._overrideManager;
                if (allDef && typeof om.clearGlobalOverrideForSource === "function") {
                    om.clearGlobalOverrideForSource(allDef.layerSource);
                }
            }

            this._assetSets = {};
            this._assetSetAssets = {};
            this._assetToSets = {};
            this._allAssetsSetId = null;
        });
    }

    public maintainAssetSet(assetId: string): void {
        if (!assetId) return;

        // Usually single asset, but can touch multiple sets; still safe to batch lightly
        this._withOverrideBatch(() => {
            const setMap = this._assetToSets[assetId];
            if (setMap) {
                for (const setId in setMap) {
                    this._applySetLayerToAssets(setId, [assetId]);
                }
            }
            this._syncAssetSetVisibilityForAsset(assetId);
        });
    }

    public syncAssetSetVisibility(
        strictIsolation: boolean = false,
        materializeLoadedVisibility: boolean = true
    ): void {
        this._withOverrideBatch(() => {
            this._strictVisibilityActive = strictIsolation;

            // Knowledge Views enforce geometry exclusion through page ownership
            // and worker filtering. Their included-but-hidden layers are handled
            // separately by MaterialOverrideManager using HidMat. Clear any
            // legacy visibility state left by an earlier call, but do not create
            // new entries here.
            if (!materializeLoadedVisibility) {
                const legacyHiddenAssets = Object.keys(this._visibilityHiddenAssets);
                if (legacyHiddenAssets.length > 0) {
                    this._meshManager.unhideObjects(legacyHiddenAssets, false, false);
                    this._visibilityHiddenAssets = {};
                }
                return;
            }

            const assetIDs = new Set([
                ...Object.keys(this._assetToSets),
                ...this._meshManager.getLoadedAssetIds(),
                ...Object.keys(this._visibilityHiddenAssets),
            ]);

            const assetsToHide: string[] = [];
            const assetsToUnhide: string[] = [];

            for (const assetId of assetIDs) {
                const shouldHide = this._shouldExcludeAssetGeometry(assetId);

                if (!shouldHide) {
                    if (this._visibilityHiddenAssets[assetId]) {
                        assetsToUnhide.push(assetId);
                    }
                    continue;
                }

                if (!this._meshManager.isAssetHidden(assetId)) {
                    assetsToHide.push(assetId);
                }
            }

            if (assetsToUnhide.length > 0) {
                this._meshManager.unhideObjects(assetsToUnhide, false, false);

                for (const assetId of assetsToUnhide) {
                    delete this._visibilityHiddenAssets[assetId];
                }
            }

            if (assetsToHide.length > 0) {
                this._meshManager.hideObjects(assetsToHide, false, false);

                for (const assetId of assetsToHide) {
                    this._visibilityHiddenAssets[assetId] = true;
                }
            }
        });
    }

    // ---------------------------------------------------------------------
    //  Knowledge View integration
    // ---------------------------------------------------------------------

    public applyKnowledgeViewAssetSets(assetSets: IKnowledgeViewAssetSet[]): void {
        this._withOverrideBatch(() => {
            const incoming = assetSets || [];
            const incomingIds: { [id: string]: boolean } = {};

            for (let i = 0; i < incoming.length; i++) {
                const kv = incoming[i];
                if (!kv || !kv.ID) continue;

                const setId = kv.ID;
                incomingIds[setId] = true;

                if (!this._assetSets[setId]) {
                    this._assetSets[setId] = {
                        id: setId,
                        priority: 0,
                        colorKey: undefined,
                        materialID: null,
                        active: true,
                        layerSource: `assetSet:${setId}`,
                        layerPriority: 0,
                        isAll3DAssets: false
                    };
                }

                const def = this._assetSets[setId];
                def.priority = kv.Ord;
                def.layerPriority = kv.Ord;
                def.active = true;
                def.colorKey = kv.Color;
                def.hidden = !!kv.Hidden;
                def.materialID = kv.MaterialID ?? null;

                if (!def.layerSource) def.layerSource = `assetSet:${setId}`;

                // Apply to explicit members (if any)
                const members = this._assetSetAssets[setId];
                if (members && setId !== this._allAssetsSetId) {
                    this._applySetLayerToAssets(setId, Object.keys(members));
                }
            }

            // Any other known sets become inactive
            for (const setId in this._assetSets) {
                if (incomingIds[setId]) continue;

                const def = this._assetSets[setId];
                if (!def || def.active === false) continue;

                def.active = false;

                if (def.isAll3DAssets) {
                    // global toggle
                    this._syncAllAssetsGlobalLayer();
                } else {
                    const members = this._assetSetAssets[setId];
                    if (members) {
                        this._applySetLayerToAssets(setId, Object.keys(members));
                    }
                }
            }

            // Finally ensure global is consistent if all-assets exists
            this._syncAllAssetsGlobalLayer();
        });
    }

    // ---------------------------------------------------------------------
    //  Internal helpers
    // ---------------------------------------------------------------------

    private _shouldSetHaveEffectiveLayer(def: IAssetSetDefinition | null | undefined): boolean {
        if (!def) return false;
        if (!def.active) return false;
        if (def.layerPriority <= 0) return false;
        return true;
    }

    private _clearAssetSetVisibility(): void {
        for (const assetId in this._visibilityHiddenAssets) {
            this._restoreVisibilityAsset(assetId);
        }
        this._visibilityHiddenAssets = {};
        this._strictVisibilityActive = false;
    }

    private _restoreVisibilityAsset(assetId: string): void {
        this._meshManager.unhideObjects([assetId], false, false);
    }

    private _shouldExcludeAssetGeometry(assetId: string): boolean {
        const memberAssetSetIDs = Object.keys(this._assetToSets[assetId] ?? {});
        const allAssetsVisible = this._allAssetsSetId
            ? this.isSetEnabled(this._allAssetsSetId)
            : false;
        const belongsToVisibleSet = memberAssetSetIDs.some(setId =>
            this.isSetEnabled(setId));

        return !allAssetsVisible &&
            (this._strictVisibilityActive || memberAssetSetIDs.length > 0) &&
            !belongsToVisibleSet;
    }

    private _syncAssetSetVisibilityForAsset(assetId: string): void {
        if (!assetId) return;

        const shouldHide = this._shouldExcludeAssetGeometry(assetId);

        if (!shouldHide) {
            if (this._visibilityHiddenAssets[assetId]) {
                this._restoreVisibilityAsset(assetId);
                delete this._visibilityHiddenAssets[assetId];
            }
            return;
        }

        // MeshManager owns the durable hidden/pending-hidden state used when
        // LOD pages unload and reload.
        if (!this._meshManager.isAssetHidden(assetId)) {
            this._meshManager.hideObjects([assetId], false, false);
            this._visibilityHiddenAssets[assetId] = true;
        }
    }

    private _applySetLayerToAssets(
        assetSetId: string,
        assetIds: string[]
    ): void {
        const def = this._assetSets[assetSetId];
        if (!def) return;

        // All-assets never uses per-asset layers (it is global)
        if (def.isAll3DAssets) return;

        const shouldApply = this._shouldSetHaveEffectiveLayer(def);
        const members = this._assetSetAssets[assetSetId] || {};

        for (let i = 0; i < (assetIds || []).length; i++) {
            const assetId = assetIds[i];
            if (!assetId) continue;

            if (!shouldApply || !members[assetId]) {

                // Clear this set's per-asset layer
                this._overrideManager.clearOverrideForSource(assetId, def.layerSource);
                continue;
            }

            // IMPORTANT PERFORMANCE NOTE:
            // You do NOT need to size this array to master mesh count because
            // MaterialOverrideManager._resolveMaterialForIndex supports "materials[0]" fallback.
            // So passing a single entry is enough (applies to all master meshes).
            const matName = this._getAssetSetMaterialName(assetSetId);

            this._overrideManager.setOverrideForSource(
                assetId,
                def.layerSource,
                def.layerPriority,
                [matName]
            );
        }
    }

    private _syncAllAssetsGlobalLayer(): void {
        const allId = this._allAssetsSetId;
        const om: any = this._overrideManager;

        if (!allId) return;

        const def = this._assetSets[allId];
        if (!def) return;

        // Must be present on override manager
        const canSet = typeof om.setGlobalOverrideForSource === "function";
        const canClear = typeof om.clearGlobalOverrideForSource === "function";
        if (!canSet || !canClear) return;

        const shouldApply = this._shouldSetHaveEffectiveLayer(def);

        // All Assets Hidden is a geometry-source decision, not a global HidMat
        // override. Explicit higher-priority sets remain loaded and decide
        // their own Default/Color/Hidden material winner.
        if (!shouldApply || def.hidden) {
            om.clearGlobalOverrideForSource(def.layerSource);
            return;
        }

        const matId = this._getAssetSetMaterialName(allId);
        om.setGlobalOverrideForSource(def.layerSource, def.layerPriority, [matId]);
    }

    private _getAssetSetMaterialName(assetSetId: string): string {
        const def = this._assetSets[assetSetId];
        if (def?.hidden) {
            return this._materialManager.getHiddenMaterialName("");
        }
        const colorKey = def ? def.colorKey : undefined;
        const materialID = def ? def.materialID : null;

        if (materialID) {
            const advancedMaterial = this._materialManager.getOrCreateAdvancedTextureMaterial(materialID, colorKey);
            if (advancedMaterial) return advancedMaterial;
        }

        return this._getMaterialNameForColorKey(colorKey);
    }

    private _getMaterialNameForColorKey(colorKey?: number): string {
        if (colorKey == null) {
            return null;
        }

        // 0 encodes RGBA #00000000 (fully transparent) — treat as "no color"
        if (colorKey === 0) {
            return null;
        }

        const mm: any = this._materialManager;
        return mm.getOrCreateHexMaterial(colorKey).id;
    }
}

export default AssetSetManager;
